import { useRef, useState, useEffect, ChangeEvent } from "react";
import C from "classnames";

import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { useAiHints } from "../../contexts/SandboxHooks";

import S from "./AspenMessage.module.scss";

interface AspenMessageProps {
  runId?: string;
  message: string;
  feedback?: { helpful?: boolean | null; comment?: string };
  highlighted?: boolean;
  highlightTime?: Date | null;
}

const AspenMessage = ({
  runId,
  message,
  feedback,
  highlighted = false,
  highlightTime = null,
}: AspenMessageProps) => {
  const isHighlightedRef = useRef<boolean>();
  const highlightTimeRef = useRef<Date | null>();
  const highlightedElementRef = useRef<HTMLDivElement>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [countingDown, setCountingDown] = useState(false);
  const [tickerValue, setTickerValue] = useState<number | null>(null);
  const [thumbValue, setThumbValue] = useState<"up" | "down" | null>(null);
  const [userComment, setUserComment] = useState("");
  const { provideHintFeedback } = useAiHints();

  const COUNTDOWN_SECONDS = 10;

  // Need to keep a ref to the countingDown state for the unmount handler
  const countingDownRef = useRef(countingDown);
  useEffect(() => {
    countingDownRef.current = countingDown;
  }, [countingDown]);

  // Handle when user clicks thumbs up or down
  const onThumbClick = (type: "up" | "down") => {
    setThumbValue(type);
    setFeedbackSubmitted(false);
    startCountdown();
  };

  // Handle the countdown timer for auto-submitting feedback
  useEffect(() => {
    let autoSubmitTimer: ReturnType<typeof setInterval> | undefined;

    if (countingDown) {
      autoSubmitTimer = setInterval(() => {
        setTickerValue(prev => {
          if (prev === null) return null;
          if (prev === 1) {
            submitFeedback();
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Clear interval when countdown stops or component unmounts
    return () => {
      if (autoSubmitTimer) {
        clearInterval(autoSubmitTimer);
      }
    };
  }, [countingDown]);

  // Handle unmount or beforeunload by submitting feedback if counting down
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (countingDownRef.current) {
        submitFeedback();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // on unmount, remove beforeunload listener, and submit feedback if counting down
      window.removeEventListener("beforeunload", handleBeforeUnload);
      handleBeforeUnload();
    };
  }, []);

  // Start a countdown to auto-submit feedback
  const startCountdown = () => {
    setTickerValue(COUNTDOWN_SECONDS);
    setCountingDown(true);
  };

  // Cancel the countdown to auto-submit feedback
  const cancelCountdown = () => {
    setCountingDown(false);
    setTickerValue(null);
  };

  // Update the user comment and cancel countdown if active
  const onCommentChange = (e: ChangeEvent<HTMLInputElement>) => {
    cancelCountdown();
    setUserComment(e.target.value);
  };

  // Actually submit the feedback
  const submitFeedback = () => {
    setCountingDown(false);
    setTickerValue(null);

    if (runId && !feedbackSubmitted) {
      provideHintFeedback({
        submissionId: runId,
        helpful: thumbValue === "up",
        comment: userComment,
      });
      setFeedbackSubmitted(true);
    }

    setFeedbackSubmitted(true);
  };

  // Scroll the highlighted hint into view when it changes to TRUE,
  // or if it's already TRUE and the highlightTime changes
  useEffect(() => {
    if (highlighted && (!isHighlightedRef.current || highlightTime !== highlightTimeRef.current)) {
      highlightedElementRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    isHighlightedRef.current = highlighted;
    highlightTimeRef.current = highlightTime;
  }, [highlighted, highlightTime]);

  // Render the component
  return (
    <div className={S.root}>
      <div className={C(S.panel, { [S.highlighted]: highlighted })} ref={highlightedElementRef}>
        <div className={C("body-sm", { ["font-bold"]: highlighted })}>{message}</div>
      </div>
      {runId && (
        <div className={C("body-sm", S.feedback)}>
          {feedback || feedbackSubmitted ? (
            <div className={S.feedbackMessage}>
              <i>
                {thumbValue === "up" || feedback?.helpful
                  ? "Thank you for submitting feedback!"
                  : "We're sorry this hint wasn't helpful. Thank you for your feedback!"}
              </i>
            </div>
          ) : (
            <>
              {thumbValue ? (
                <>
                  <div className={S.feedbackMessage}>
                    <i>
                      We appreciate your feedback! If you would like, you can add an optional
                      comment below.
                    </i>
                    <div className="flex gap-2 align-items-center mt-2">
                      <InputText
                        maxLength={200}
                        className={C(S.commentBox, "p-inputtext-sm")}
                        placeholder="Add your optional comment here..."
                        value={userComment}
                        onFocus={cancelCountdown}
                        onBlur={startCountdown}
                        onChange={onCommentChange}
                      />
                      <Button
                        icon={!countingDown ? "pi pi-check" : undefined}
                        size="small"
                        className={C(S.submitBtn, { [S.iconOnly]: !countingDown })}
                        aria-label="Submit feedback"
                        onClick={submitFeedback}
                      >
                        {countingDown && <span>Submitting in {tickerValue}...</span>}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex gap-1">
                  <Button
                    className={C(S.feedbackBtn)}
                    icon="pi pi-thumbs-up"
                    size="small"
                    text
                    aria-label="Thumbs up"
                    onClick={() => onThumbClick("up")}
                  />
                  <Button
                    className={C(S.feedbackBtn)}
                    icon="pi pi-thumbs-down"
                    size="small"
                    text
                    aria-label="Thumbs down"
                    onClick={() => onThumbClick("down")}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AspenMessage;
